import React from 'react';
import { Link } from 'react-router-dom';

const Tutorials = () => {
  const tutorials = [
    {
      title: 'Getting Started with JSON Format',
      description: 'Learn how to structure your answer keys and student work in JSON format for optimal grading results.',
      steps: [
        'Create a JSON array with question objects',
        'Each question should have: questionNumber, question, answer, value, and optional rubric',
        'Structure student work similarly with questionNumber and answer fields',
        'Use the Load Example button to see a sample format'
      ]
    },
    {
      title: 'Using Natural Language Input',
      description: 'Write your assignments in plain English and let the system parse them automatically.',
      steps: [
        'Select "Natural" input format',
        'Write questions in a clear, structured format',
        'Use keywords like "Question 1:", "Answer:", "Points:" to help parsing',
        'The system will automatically detect question types (multiple choice, essay, etc.)'
      ]
    },
    {
      title: 'Grading with Visual Input (OCR)',
      description: 'Upload images of handwritten or typed assignments and let OCR extract the text.',
      steps: [
        'Select "Visual" input format',
        'Upload clear images of student work',
        'Wait for OCR processing to extract text',
        'Review and edit extracted text if needed before grading'
      ]
    },
    {
      title: 'Optimizing AI Grading Results',
      description: 'Get the best grading results by configuring your settings and providing clear rubrics.',
      steps: [
        'Configure your preferred AI provider in Settings',
        'Set similarity thresholds based on your grading standards',
        'Provide detailed rubrics for better contextual analysis',
        'Review AI feedback and adjust scores as needed'
      ]
    },
    {
      title: 'Understanding Grading Curves',
      description: 'Use grading curves to adjust scores based on class performance.',
      steps: [
        'Enable grading curve in Settings',
        'Choose curve type: Linear, Exponential, or Logarithmic',
        'Set target average and minimum score',
        'Adjust curve strength to fine-tune adjustments'
      ]
    },
    {
      title: 'Batch Processing Multiple Assignments',
      description: 'Grade multiple students\' work efficiently using batch processing features.',
      steps: [
        'Structure your student work JSON with multiple student entries',
        'Use consistent question numbering across all students',
        'Review batch results summary for quick overview',
        'Export results for record keeping'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent tracking-tight">
            Tutorials
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 font-light tracking-wide">
            Learn how to get the most out of the AI Grading Agent
          </p>
        </div>

        {/* Tutorials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {tutorials.map((tutorial, index) => (
            <div 
              key={index}
              className="premium-card p-6 animate-slide-up hover:scale-[1.02] transition-transform duration-200"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-purple-600/20 flex items-center justify-center">
                  <span className="text-primary font-bold text-lg">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {tutorial.title}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                    {tutorial.description}
                  </p>
                  <div className="space-y-2">
                    {tutorial.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex gap-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                          <div className="w-2 h-2 rounded-full bg-primary"></div>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                          {step}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tips Section */}
        <div className="premium-card p-8 mb-12 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10 border-amber-200/60 dark:border-amber-800/40">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
            <span className="text-2xl">💡</span>
            Pro Tips
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <span className="text-primary font-bold">•</span>
              <p className="text-gray-700 dark:text-gray-300">Use clear, specific rubrics for consistent grading across all questions</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary font-bold">•</span>
              <p className="text-gray-700 dark:text-gray-300">Review AI feedback before finalizing scores - it's a tool to assist, not replace judgment</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary font-bold">•</span>
              <p className="text-gray-700 dark:text-gray-300">Try different input formats for various assignment types to find what works best</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary font-bold">•</span>
              <p className="text-gray-700 dark:text-gray-300">Save successful grading templates for reuse with similar assignments</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary font-bold">•</span>
              <p className="text-gray-700 dark:text-gray-300">Adjust similarity thresholds based on your grading standards and assignment difficulty</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary font-bold">•</span>
              <p className="text-gray-700 dark:text-gray-300">Use batch processing for large classes to save time and maintain consistency</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary-light font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to App
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Tutorials;
